import { useEffect, useState } from 'react';
import {
  CompanyMessage,
  deleteCompanyMessage,
  fetchAllCompanyMessages,
} from '../../services/dataService';
import MessageModal from './MessageModal';
import DataTable from '../shared/DataTable';
import { ColDef, DomLayoutType, ICellRendererParams } from 'ag-grid-community';
import { formatValidityRange } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import Button from '../shared/Button';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useNotification } from '../../contexts/NotificationContext';
import { Delete, Edit } from '@mui/icons-material';
interface MessagesTabProps {
  companyId: string;
}

const MessagesTab = ({ companyId }: MessagesTabProps) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [messages, setMessages] = useState<CompanyMessage[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<CompanyMessage | null>(null);

  useEffect(() => {
    loadMessages();
  }, [companyId]);

  const loadMessages = async () => {
    try {
      const data = await fetchAllCompanyMessages(companyId);
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleDeleteMessage = (message: CompanyMessage) => {
    setSelectedMessage(message);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (selectedMessage) {
      await deleteCompanyMessage(selectedMessage.id);
      showNotification(t('company.messages_tab.message_deleted'), 'success');
      loadMessages();
      setIsDeleteModalOpen(false);
    }
  };

  const handleEditMessage = (message: CompanyMessage) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const handleCreateMessage = () => {
    setSelectedMessage(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  const handleMessageSaved = () => {
    showNotification(t('company.messages_tab.message_saved'), 'success');
    loadMessages();
    handleModalClose();
  };

  const columnDefs: ColDef<CompanyMessage>[] = [
    {
      headerName: t('company.messages_tab.type'),
      field: 'type',
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<CompanyMessage>) => {
        const type = params.value;
        return (
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
            ${
              type === 'warning'
                ? 'bg-red-100 text-red-800'
                : type === 'notice'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-green-100 text-green-800'
            }`}
          >
            {t(`company.messages_tab.message_types.${type}`)}
          </span>
        );
      },
    },
    {
      field: 'message',
      headerName: t('company.messages_tab.message'),
      sortable: true,
      filter: true,
    },
    {
      headerName: t('company.messages_tab.validity_range'),
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (!params.data?.validity_range) return '-';
        return formatValidityRange(params.data.validity_range);
      },
    },
    {
      headerName: t('company.messages_tab.created_by'),
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (!params.data?.creator_details) return '-';
        const creator = params.data.creator_details as { user_name?: string; user_email?: string };
        if (creator.user_name) return creator.user_name;
        if (creator.user_email) return creator.user_email;
        return '-';
      },
    },
    {
      headerName: t('common.actions'),
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<CompanyMessage>) => {
        if (!params.data) return null;
        return (
          <>
            <button
              onClick={() => handleEditMessage(params.data as CompanyMessage)}
              className="text-blue-600 hover:text-blue-900"
            >
              <Edit className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleDeleteMessage(params.data as CompanyMessage)}
              className="text-red-600 hover:text-red-900"
            >
              <Delete className="h-5 w-5" />
            </button>
          </>
        );
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4 flex-shrink-0">
        <h3 className="text-xl font-semibold">{t('company.messages_tab.title')}</h3>
        <Button onClick={handleCreateMessage}>{t('company.messages_tab.create_message')}</Button>
      </div>

      <div className="bg-white rounded-sm shadow flex-grow min-h-0">
        <DataTable<CompanyMessage>
          rowData={messages}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          theme="ag-theme-alpine"
          noRowsMessage={t('company.messages_tab.no_messages')}
          height={'100%'}
        />
      </div>

      {isModalOpen && (
        <MessageModal
          companyId={companyId}
          message={selectedMessage}
          onClose={handleModalClose}
          onSaved={handleMessageSaved}
        />
      )}
      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t('company.messages_tab.delete_message')}
        message={t('company.messages_tab.delete_message_message')}
      />
    </div>
  );
};

export default MessagesTab;
